import React, { useState, useRef } from "react";
import { Row, Col, Card, Button, Input, Drawer, Form, Select, message } from "antd";
import { SearchOutlined, UserOutlined, CalendarOutlined, EyeOutlined } from "@ant-design/icons";
import { Scrollbars } from "react-custom-scrollbars";
import Keyboard from "react-simple-keyboard";
import sortBy from "lodash/sortBy";
import dayjs from "dayjs";
import Close from "../../../../../assets/images/close-x.svg";
import wallet from "../../../../../assets/images/wallet.svg";
import credit from "../../../../../assets/images/creditIcon.svg";
import cash from "../../../../../assets/images/cash.svg";
import cardType from "../../../../../assets/images/cardType.svg";
import UPI from "../../../../../assets/images/upiIcon.svg";
import CancelArrow from "../../../../../assets/images/cancelArrow.svg";
import messages from "../../../../../errors/messages.en-IN.json";
import useAuthStore from "../../../../../stores/useAuthStore";
import useCartStore from "../../../../../stores/useCartStore";
import useTransactionStore from "../../../../../stores/useTransactionStore";
import { useWindowDimensions } from "../../../../../lib/useViewport";

const { Option } = Select;

const CustomerWalletDrawer = ({
  showCustomerWallet,
  setShowCustomerWallet,
  selectedCustomer,
  setSelectedCustomer,
  customersData,
  setCustomersData,
  showPayments,
  setShowPayments,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  posConfig,
  cart,
  setCart,
  currenciesData,
  denaminationsKeyboard,
  setDenaminationsKeyboard,
  filteredPaymentMethods,
  requestPayment,
  fetchWalletData,
  themeJSON,
  currenciesList,
  pricePrecision,
  setIsBankTransferOpen,
  setBankTransferContext,
  enterTotalQtyForWallet,
  currentCardInput,
  setCurrentCardInput,
  topupAmount,
  setTopupAmount,
  showRefundModal,
  setShowRefundModal,
  handleWalletRefundComplete,
  currentUPIInput,
  setCurrentUPIInput,
}) => {
  const [cardPaymentForm] = Form.useForm();
  const [inputFocused, setInputFocused] = useState("");
  const authorizationRef = useRef(null);
  const keyboardRef = useRef(null);
  const topupInputRef = useRef(null);
  const cashInputRef = useRef(null);
  const cardInputRef = useRef(null);
  const upiInputRef = useRef(null);

  const [customerSearchValue, setCustomerSearchValue] = useState("");
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  // const [topupAmount, setTopupAmount] = useState("");
  const [isProcessingTopup, setIsProcessingTopup] = useState(false);
  const [cashTendered, setCashTendered] = useState(0);
  const [currentCashInput, setCurrentCashInput] = useState("");
  const [showCardAmountInput, setShowCardAmountInput] = useState(false);
  const [showCardDetailsModal, setShowCardDetailsModal] = useState(false);
  // const [currentCardInput, setCurrentCardInput] = useState("");
  // const [currentUPIInput, setCurrentUPIInput] = useState("");
  const [focusedInputField, setFocusedInputField] = useState(null);
  const [layoutName, setLayoutName] = useState("default");
  // showRefundModal + setShowRefundModal now received from props (Core/index.js) — single source of truth

  const tillData = useAuthStore((s) => s.tillData);
  const { width } = useWindowDimensions();
  const [dynamicPaymentInput, setDynamicPaymentInput] = useState("");

  const modulo = (a, b) => a - b * Math.floor(a / b);

  const rawWalletPayments = useTransactionStore((s) => s.walletPayments) || [];
  const matchedPayments = rawWalletPayments.map((payment) => {
    const matchedMethod = showPayments?.find((method) => method.finPaymentmethodId === payment.finPaymentmethodID);
    return {
      ...matchedMethod,
      ...payment,
      amount: Number(payment.amount).toFixed(2),
    };
  });

  const handleCustomerSearch = (value) => {
    setCustomerSearchValue(value);
  };

  const filteredCustomers =
    Array.isArray(customersData) && customersData.length > 0
      ? customersData.filter(
          (customer) =>
            customer?.name?.toLowerCase().includes(customerSearchValue.toLowerCase()) ||
            customer?.phone?.includes(customerSearchValue) ||
            customer?.id?.toLowerCase().includes(customerSearchValue.toLowerCase())
        )
      : cart?.customer
        ? [cart.customer]
        : [];

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleTopupWallet = () => {
    const initialPayments = sortBy(filteredPaymentMethods, "sequenceNo");
    setShowTopupModal(true);
    setShowTransactionsModal(false);
    setShowPayments(initialPayments);
    setDenaminationsKeyboard(true);
    // Auto-focus the Top-up Amount input so the user can type immediately
    // (without this they had to click the field first — issue #17).
    setTimeout(() => {
      topupInputRef.current?.focus();
      setFocusedInputField("topupAmount");
    }, 50);
  };

  const handleViewTransactions = () => {
    setShowTransactionsModal(true);
    setShowTopupModal(false);
  };

  const handleWalletRefund = () => {
    setShowRefundModal(true);
    setShowTopupModal(false);

    const allMethods = tillData?.tillAccess?.csBunit?.paymentMethodList || [];
    let returnPayments = [];
    try {
      const raw = tillData?.tillAccess?.walletPreferences;
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      const prefs = Array.isArray(parsed) ? parsed[0] : parsed;
      const settings = prefs?.paymentsettings;
      if (Array.isArray(settings) && settings.length > 0) {
        const refundIds = settings.filter((s) => s.refund === "Y").map((s) => s.fin_paymentmethod_id);
        if (refundIds.length > 0) {
          returnPayments = sortBy(
            allMethods.filter((pm) => refundIds.includes(pm.finPaymentmethodId)),
            ["sequenceNo", "name"]
          );
        }
      }
    } catch {
      /* parse error — use fallback */
    }
    if (returnPayments.length === 0) {
      returnPayments = sortBy(allMethods.filter((pm) => pm.isReturns === "Y" && pm.isWallet !== "Y") || [], "sequenceNo");
    }

    if (returnPayments.length > 0) {
      const firstReturnMethod = returnPayments[0];
      setSelectedPaymentMethod(firstReturnMethod);
      setDenaminationsKeyboard(firstReturnMethod.name?.toLowerCase() === "cash");
    } else {
      setSelectedPaymentMethod({});
      setDenaminationsKeyboard(false);
    }

    // Focus amount input
    setTimeout(() => {
      topupInputRef.current?.focus();
      setFocusedInputField("topupAmount");
    }, 50);
  };

  const formatOnBlur = () => {
    if (!topupAmount) return;
    setTopupAmount(parseFloat(topupAmount).toFixed(pricePrecision));
  };

  const handleTopupAmountChange = (value) => {
    const sanitizedValue = value.replace(/[^0-9.]/g, "");
    const parts = sanitizedValue.split(".");
    if (parts.length > 2) return;
    if (parts.length === 2 && parts[1].length > 2) return;
    const numericValue = parseFloat(sanitizedValue || 0);

    if (showRefundModal) {
      const balance = selectedCustomer?.[0]?.currentBalance || 0;
      if (balance <= 0) {
        message.error("No balance available for refund");
        return;
      }
      if (numericValue > balance) {
        message.warning(`Maximum refundable amount is ${balance.toFixed(2)}`);
        return;
      }
    }

    setTopupAmount(sanitizedValue);
    if (sanitizedValue === "") {
      setCurrentCashInput("");
      setCurrentCardInput("");
      setCurrentUPIInput("");
      setDynamicPaymentInput("");
      setCashTendered(0);
      return;
    }
    autoFillEnterAmount(sanitizedValue);
  };

  const autoFillEnterAmount = (amount) => {
    if (!selectedPaymentMethod) return;
    if (amount === "" || isNaN(amount)) return;
    const totalPaid = matchedPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const remaining = parseFloat(amount) - totalPaid;
    if (remaining <= 0) return;
    const value = remaining.toString();
    if (selectedPaymentMethod.name === "Cash") setCurrentCashInput(value);
    else if (selectedPaymentMethod.name === "Card") setCurrentCardInput(value);
    else if (selectedPaymentMethod.name === "UPI") setCurrentUPIInput(value);
    else setDynamicPaymentInput(value);
  };

  const handleDenominationClick = (value) => {
    const sanitizedValue = Number.parseFloat(value.toString().replace(/[^0-9.]/g, "")) || 0;
    if (focusedInputField === "topupAmount") {
      const currentValue = Number.parseFloat(topupAmount) || 0;
      setTopupAmount((currentValue + sanitizedValue).toFixed(2));
    } else if (focusedInputField === "currentCashInput") {
      const currentValue = Number.parseFloat(currentCashInput) || 0;
      setCurrentCashInput((currentValue + sanitizedValue).toFixed(2));
    } else if (focusedInputField === "currentCardInput") {
      const currentValue = Number.parseFloat(currentCardInput) || 0;
      setCurrentCardInput((currentValue + sanitizedValue).toFixed(2));
    } else if (focusedInputField === "currentUPIInput") {
      const currentValue = Number.parseFloat(currentUPIInput) || 0;
      setCurrentUPIInput((currentValue + sanitizedValue).toFixed(2));
    }
    setDenaminationsKeyboard(true);
  };

  const handleTotalQty = (value) => {
    let currentValue =
      focusedInputField === "topupAmount"
        ? topupAmount
        : selectedPaymentMethod.name === "Cash"
          ? currentCashInput
          : selectedPaymentMethod.name === "Card"
            ? currentCardInput
            : currentUPIInput;

    if (value === "clear") {
      if (focusedInputField === "topupAmount") setTopupAmount("");
      else if (selectedPaymentMethod.name === "Cash") setCurrentCashInput("");
      else if (selectedPaymentMethod.name === "Card") setCurrentCardInput("");
      else setCurrentUPIInput("");
    } else if (value === "x") {
      if (focusedInputField === "topupAmount") setTopupAmount(currentValue.slice(0, -1) || "");
      else if (selectedPaymentMethod.name === "Cash") setCurrentCashInput(currentValue.slice(0, -1) || "");
      else if (selectedPaymentMethod.name === "Card") setCurrentCardInput(currentValue.slice(0, -1) || "");
      else setCurrentUPIInput(currentValue.slice(0, -1) || "");
    } else {
      const newValue = currentValue + value;
      const sanitizedValue = newValue.replace(/[^0-9.]/g, "");
      const parts = sanitizedValue.split(".");
      if (parts.length > 2 || (parts.length === 2 && parts[1].length > 2)) return;
      if (focusedInputField === "topupAmount") setTopupAmount(sanitizedValue);
      else if (selectedPaymentMethod.name === "Cash") setCurrentCashInput(sanitizedValue);
      else if (selectedPaymentMethod.name === "Card") setCurrentCardInput(sanitizedValue);
      else setCurrentUPIInput(sanitizedValue);
    }
  };

  const handleCardDetailsSubmit = async () => {
    if (isProcessingTopup) return;
    try {
      await cardPaymentForm.validateFields();
      const cardValues = cardPaymentForm.getFieldsValue();
      localStorage.setItem("cardPaymentDetails", JSON.stringify(cardValues));
      setShowCardDetailsModal(false);
      setIsProcessingTopup(true);
      const paymentAmount = Number.parseFloat(currentCardInput).toFixed(2);
      const totalPaymentsBefore = matchedPayments.reduce((sum, payment) => sum + Number.parseFloat(payment.amount || 0), 0).toFixed(2);
      const topupTotal = Number.parseFloat(topupAmount).toFixed(2);
      await requestPayment(selectedPaymentMethod, paymentAmount, undefined, true);
      useCartStore.getState().setCart(cart);
      const updatedTotalPayments = (Number.parseFloat(totalPaymentsBefore) + Number.parseFloat(paymentAmount)).toFixed(2);
      setCurrentCardInput("");
      const remainingAmount = Number.parseFloat(topupTotal) - Number.parseFloat(updatedTotalPayments);
      if (remainingAmount > 0) {
        autoFillEnterAmount(remainingAmount.toString());
      }
      if (Number.parseFloat(updatedTotalPayments) >= Number.parseFloat(topupTotal)) {
        await handleWalletTopUp(topupAmount);
      } else {
        message.success(`Card payment of ${paymentAmount} added. Remaining: ${remainingAmount.toFixed(2)}`);
      }
      cardPaymentForm.resetFields();
      setIsProcessingTopup(false);
    } catch {
      message.error(`${messages.info.all_required}`);
      setIsProcessingTopup(false);
    }
  };

  const handleCardPayment = () => {
    if (!topupAmount || Number.parseFloat(topupAmount) < 0) {
      message.error(`${messages.info.valid_topup_amount}`);
      return;
    }
    if (posConfig?.transactionID === "Y") {
      setShowCardAmountInput(true);
    }
  };

  const handleShift = () => {
    setLayoutName(layoutName === "shift" ? "default" : "shift");
  };

  const handleKeyboardInputs = (button) => {
    const currentValue = cardPaymentForm.getFieldValue(inputFocused) || "";
    if (button === "{space}") {
      cardPaymentForm.setFieldsValue({ [inputFocused]: `${currentValue} ` });
    } else if (button === "{backspace}") {
      cardPaymentForm.setFieldsValue({ [inputFocused]: currentValue.slice(0, -1) });
    } else {
      cardPaymentForm.setFieldsValue({ [inputFocused]: currentValue + button });
    }
  };

  const handleWalletTopUp = async (amount) => {
    if (!cart?.customer?.cwrCustomerId) {
      message.error(`${messages.errors.customer.not_selected}`);
      return;
    }
    const paymentAmount = Number.parseFloat(amount || 0);
    const storedPayments = useTransactionStore.getState().walletPayments;
    if (!paymentAmount || isNaN(paymentAmount) || paymentAmount <= 0) {
      message.error(`${messages.info.invalid_topup}`);
      return;
    }
    // Block topup if no payments collected — without this guard the server
    // received empty payments[] and the wallet got credited without an actual
    // payment trail. (Issue #18)
    // if (!Array.isArray(storedPayments) || storedPayments.length === 0) {
    //   message.error("Please add at least one payment before completing the top-up.");
    //   return;
    // }
    const { upsertWalletTransactionService } = await import("../../../../../services");
    const db = (await import("../../../../../database")).default;
    const payload = {
      bunitId: tillData.tillAccess.csBunit.csBunitId,
      customerId: cart.customer.cwrCustomerId,
      trxType: "DP",
      trxValue: paymentAmount,
      tillId: tillData?.tillAccess.cwrTill.cwrTillID,
      payments: (storedPayments || []).map(({ count, isBankTransfer, ...p }) => ({
        finPaymentmethodID: p.finPaymentmethodID || p.finPaymentmethodId,
        amount: p.amount,
        ...(p.isBankTransfer === "Y" && {
          financialAccountId: p?.financialAccountId || p?.financialAccount || null,
          transactionTime: p.transactionTime || (p.transactionDate ? dayjs(p.transactionDate).format("YYYY-MM-DD") : null),
          imageUrl: p.imageUrl || p?.financialFile || null,
          transactionId: p?.transactionId || null,
        }),
      })),
      transactionDetails: { paymnet: paymentAmount },
    };
    try {
      const response = await upsertWalletTransactionService(payload);
      if (response?.status === "200") {
        await db?.tillEvents
          .where("tillStatus")
          .equals("open")
          .modify((event) => {
            const walletTransactions = [...(event.walletTransactions || [])];
            storedPayments.forEach((payment) => {
              const existingIndex = walletTransactions.findIndex((wt) => wt.finPaymentmethodID === payment.finPaymentmethodID);
              if (existingIndex !== -1) {
                walletTransactions[existingIndex].amount += payment.amount;
              } else {
                walletTransactions.push({ ...payment });
              }
            });
            event.walletTransactions = walletTransactions;
          });
        message.success(response.message);
        fetchWalletData();
        setShowTopupModal(false);
        setTopupAmount("");
        setCurrentCashInput("");
        setCurrentCardInput("");
        setCurrentUPIInput("");
        setDynamicPaymentInput("");
        setCashTendered(0);
        setShowCardAmountInput(false);
        setShowCardDetailsModal(false);
        cardPaymentForm.resetFields();
        useTransactionStore.getState().setWalletPayments([]);
        localStorage.removeItem("cardPaymentDetails");
      } else {
        message.error(response?.message || `${messages.info.topup_failed}`);
      }
    } catch (err) {
      console.error("Failed to top up wallet:", err);
      message.error(`${messages.info.topup_record_failed}`);
    }
  };

  // Wrap the Core handler to also reset drawer-local state after successful refund
  const handleWalletRefundWithLocalCleanup = async (amount) => {
    await handleWalletRefundComplete(amount);
    setShowCustomerWallet(false);
    setShowCardAmountInput(false);
    setShowCardDetailsModal(false);
  };

  const enterTotalQty = async () => {
    if (isProcessingTopup) return;
    if (!selectedPaymentMethod || !selectedPaymentMethod.name) {
      message.warning("Please select a payment method to complete the payment");
      return;
    }

    if (typeof setCart !== "function") {
      console.error("setCart is not a function. Cannot update cart.");
      message.error(`${messages.errors.payments.configuration_payment_failed}`);
      return;
    }

    const currentInput =
      selectedPaymentMethod.name === "Cash"
        ? currentCashInput
        : selectedPaymentMethod.name === "Card"
          ? currentCardInput
          : selectedPaymentMethod.name === "UPI"
            ? currentUPIInput
            : dynamicPaymentInput; // for NEFT / RTGS / others
    if (!currentInput || Number.parseFloat(currentInput) <= 0) {
      message.error(`${messages.info.valid_amount}`);
      return;
    }
    if (!topupAmount || Number.parseFloat(topupAmount) <= 0) {
      message.error(`${messages.info.valid_topup_amount}`);
      return;
    }
    const topupTotal = Number.parseFloat(topupAmount).toFixed(2);
    const totalPayments = matchedPayments.reduce((sum, payment) => sum + Number.parseFloat(payment.amount || 0), 0).toFixed(2);
    const remaining = Number.parseFloat(topupTotal) - Number.parseFloat(totalPayments);
    const rawPayment = Number.parseFloat(currentInput);

    if (rawPayment > remaining) {
      message.warning(`${selectedPaymentMethod.name} amount exceeds remaining ${showRefundModal ? "refund" : "top-up"} amount of ${remaining.toFixed(2)}`);
      return;
    }

    const paymentAmount = rawPayment.toFixed(2);
    const newCashTendered = (Number.parseFloat(cashTendered) || 0) + Number.parseFloat(paymentAmount);

    if (Number.parseFloat(paymentAmount) <= 0) {
      message.error(`${selectedPaymentMethod.name} ${messages.warnings.payment.payment_multiple_check_topup} ${remaining.toFixed(2)}`);
      return;
    }
    setIsProcessingTopup(true);
    try {
      if (selectedPaymentMethod.name === "Cash") {
        const filteredCurrencies = tillData?.tillAccess?.csBunit?.currencies[0]?.denominations?.sort((a, b) => a.value - b.value) || [];
        if (filteredCurrencies.length > 0) {
          const result = modulo(Number.parseFloat(currentInput || 0), filteredCurrencies[0]?.value || 1);
          if (result !== 0) {
            message.warning(`${messages.warnings.payment.payment_multiple_check} ${filteredCurrencies[0]?.value}`);
            setIsProcessingTopup(false);
            return;
          }
        }
      }
      if (selectedPaymentMethod?.name?.toLowerCase() === "card" && !selectedPaymentMethod?.integratedPayment && posConfig?.transactionID === "Y") {
        localStorage.setItem(
          "walletDataForCardIntegration",
          JSON.stringify({
            paymentAmount,
            topupTotal,
            totalPayments,
            newCashTendered,
            selectedPaymentMethod,
            selectedCustomer,
            customersData,
            showRefundModal,
            currenciesList,
            topupAmount,
          })
        );
        setShowCardDetailsModal(true);
        setIsProcessingTopup(false);
        return;
      }
      await requestPayment(selectedPaymentMethod, paymentAmount, undefined, true);
      useCartStore.getState().setCart(cart);
      const updatedTotalPayments = (Number.parseFloat(totalPayments) + Number.parseFloat(paymentAmount)).toFixed(2);
      const newTransaction = {
        id: (selectedCustomer?.transactions?.length || 0) + 1,
        date: new Date().toISOString().split("T")[0],
        time: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }),
        type: "Credit",
        amount: Number.parseFloat(paymentAmount),
        description: `Wallet Top-up via ${selectedPaymentMethod.name}`,
        reference: `TXN${String(Date.now()).slice(-3)}`,
        balance: (selectedCustomer?.currentBalance || 0) + Number.parseFloat(paymentAmount),
      };
      const updatedCustomer = {
        ...selectedCustomer,
        currentBalance: (selectedCustomer?.currentBalance || 0) + Number.parseFloat(paymentAmount),
        lastTransaction: new Date().toISOString().split("T")[0],
        transactions: [newTransaction, ...(selectedCustomer?.transactions || [])],
      };
      if (typeof setCustomersData === "function") {
        if (Array.isArray(customersData)) {
          const updatedCustomers = customersData.map((customer) => (customer?.id === selectedCustomer?.id ? updatedCustomer : customer));
          setCustomersData(updatedCustomers);
        } else {
          setCustomersData(updatedCustomer);
        }
      }
      setSelectedCustomer(updatedCustomer);
      setCurrentCashInput("");
      setCurrentCardInput("");
      setCurrentUPIInput("");
      setDynamicPaymentInput("");
      setCashTendered(newCashTendered);
      const remainingAmount = Number.parseFloat(topupTotal) - Number.parseFloat(updatedTotalPayments);

      if (remainingAmount > 0) {
        autoFillEnterAmount(remainingAmount.toString());
      } else {
        setCurrentCashInput("");
        setCurrentCardInput("");
        setCurrentUPIInput("");
        setDynamicPaymentInput("");
      }
      // Check if total payments equal topupAmount
      if (Number.parseFloat(updatedTotalPayments) >= Number.parseFloat(topupTotal)) {
        if (showRefundModal) {
          const refundAmount = Number.parseFloat(topupAmount || 0);
          const updatedTotal = Number.parseFloat(updatedTotalPayments);
          const customerBalance = selectedCustomer?.[0]?.currentBalance || 0;

          // Cannot refund more than wallet balance
          if (refundAmount > customerBalance) {
            message.error("Refund amount cannot exceed wallet balance");
            setIsProcessingTopup(false);
            return;
          }

          // Prevent over refund payment
          if (updatedTotal > refundAmount) {
            message.error("Payment exceeds refund amount");
            setIsProcessingTopup(false);
            return;
          }

          // Only complete when FULLY PAID
          if (updatedTotal === refundAmount) {
            await handleWalletRefundWithLocalCleanup(refundAmount);
          } else {
            message.success(`${selectedPaymentMethod.name} payment of ${paymentAmount} added. Remaining: ${(refundAmount - updatedTotal).toFixed(2)}`);
            setIsProcessingTopup(false);
          }

          return;
        } else {
          await handleWalletTopUp(topupAmount);
        }
      } else {
        message.success(
          `${selectedPaymentMethod.name} payment of ${currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""}${paymentAmount}${
            currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""
          } added. Remaining: ${currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""}${(topupTotal - updatedTotalPayments).toFixed(2)}${
            currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""
          }`
        );
        setIsProcessingTopup(false);
      }
    } catch (error) {
      console.error(`${selectedPaymentMethod.name} top-up error:`, error);
      message.error(`${selectedPaymentMethod?.name || "Payment"} ${messages.errors.payments.wallet_topup_failed}`);
      setIsProcessingTopup(false);
    }
  };

  const handleGenericAmount = (value) => {
    setDynamicPaymentInput((prev) => {
      if (value === "x") return prev.slice(0, -1);
      if (value === "clear") return "";
      if (value === "." && prev.includes(".")) return prev;
      if (value === "00" && prev !== "") return prev + "00";
      return prev + value;
    });
  };

  const handleDynamicPaymentInput = (value) => {
    setDynamicPaymentInput((prev) => {
      if (value === "x") return prev.slice(0, -1);
      if (value === "clear") return "";
      if (value === "." && prev.includes(".")) return prev;
      return prev + value;
    });
  };

  const renderGenericWalletKeypad = () => {
    return (
      <Row style={{ padding: "0 1% 0 1%" }} gutter={[8, 6]}>
        <Col span={6}>
          <button id="sm-amount-button1" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGenericAmount("1")}>
            1
          </button>
        </Col>
        <Col span={6}>
          <button id="sm-amount-button2" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGenericAmount("2")}>
            2
          </button>
        </Col>
        <Col span={6}>
          <button id="sm-amount-button3" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGenericAmount("3")}>
            3
          </button>
        </Col>
        <Col span={6}>
          <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor, display: "flex", alignItems: "center", justifyContent: "center" }} id="sm-amount-buttonRemove" onClick={() => handleGenericAmount("x")}>
            <img src={CancelArrow} alt="back space" width={width <= 1024 ? 20 : 25} height={width <= 1024 ? 15 : 15} />
          </button>
        </Col>
        <Col span={6}>
          <button id="sm-amount-button4" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGenericAmount("4")}>
            4
          </button>
        </Col>
        <Col span={6}>
          <button id="sm-amount-button5" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGenericAmount("5")}>
            5
          </button>
        </Col>
        <Col span={6}>
          <button id="sm-amount-button6" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGenericAmount("6")}>
            6
          </button>
        </Col>
        <Col span={6}>
          <button
            id="sm-amount-buttonClear"
            className="amt-dial-btn-market"
            style={{ background: themeJSON?.keyBoardColor }}
            onClick={() => setDenaminationsKeyboard(!denaminationsKeyboard)}
          >
            1,2,3...
          </button>
        </Col>
        <Col span={18}>
          <Row gutter={[8, 6]}>
            <Col span={8}>
              <button id="sm-amount-button7" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGenericAmount("7")}>
                7
              </button>
            </Col>
            <Col span={8}>
              <button id="sm-amount-button8" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGenericAmount("8")}>
                8
              </button>
            </Col>
            <Col span={8}>
              <button id="sm-amount-button0" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGenericAmount("9")}>
                9
              </button>
            </Col>
            <Col span={8}>
              <button id="sm-amount-button0" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGenericAmount("0")}>
                0
              </button>
            </Col>
            <Col span={8}>
              <button id="sm-amount-button00" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGenericAmount("00")}>
                00
              </button>
            </Col>
            <Col span={8}>
              <button id="sm-amount-button." className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGenericAmount(".")}>
                .
              </button>
            </Col>
          </Row>
        </Col>
        <Col span={6}>
          <button
            id="sm-amount-buttonEnter"
            className="amt-dial-btn-market2"
            disabled={isProcessingTopup}
            style={{ background: themeJSON?.keyBoardColor }}
            onClick={async () => {
              if (!topupAmount || parseFloat(topupAmount) <= 0) {
                message.warning("Please enter valid amount");
                return;
              }

              if (!dynamicPaymentInput || parseFloat(dynamicPaymentInput) <= 0) {
                message.warning("Please enter valid payment amount");
                return;
              }

              await enterTotalQty();
              setDynamicPaymentInput(""); // clear after adding
            }}
          >
            Enter
          </button>
        </Col>
      </Row>
    );
  };

  // Restore showPayments back to the initial slice (first 5 methods sorted
  // by sequenceNo). Without this, the wallet drawer's setShowPayments
  // (showing all payment methods for topup) leaks back to the right panel
  // and the buttons render misaligned. (Issue #22)
  const restoreInitialPayments = () => {
    if (typeof setShowPayments !== "function" || !Array.isArray(filteredPaymentMethods)) return;
    const sorted = sortBy(filteredPaymentMethods || [], "sequenceNo");
    setShowPayments(sorted.length > 5 ? sorted.slice(0, 5) : sorted);
  };

  const handleCancelTopup = () => {
    setShowTopupModal(false);
    setTopupAmount("");
    setCurrentCashInput("");
    setCurrentCardInput("");
    setCurrentUPIInput("");
    setDynamicPaymentInput("");
    setCashTendered(0);
    setShowCardAmountInput(false);
    setShowCardDetailsModal(false);
    cardPaymentForm.resetFields();
    useTransactionStore.getState().setWalletPayments([]);
    localStorage.removeItem("giftCardIntegrationTopupData");
    setDenaminationsKeyboard(true);
    restoreInitialPayments();
  };

  return (
    <Drawer
      className="customerWalletDrawer"
      open={showCustomerWallet}
      closable={false}
      maskClosable={false}
      onClose={() => {
        setShowCustomerWallet(false);
        setSelectedCustomer(null);
        setCustomerSearchValue("");
        setShowTopupModal(false);
        setShowTransactionsModal(false);
        setShowRefundModal(false);
        setTopupAmount("");
        setDynamicPaymentInput("");
        restoreInitialPayments();
      }}
      placement="right"
      width="62%"
      headerStyle={{ background: "white", borderBottom: "none", fontFamily: "Inter" }}
      styles={{
        body: {
          padding: "0.5rem",
          paddingTop: 0,
          background: "white",
          fontFamily: "Inter",
          overflow: "hidden",
          height: "100%",
        },
      }}
      style={{ borderRadius: "15px", fontFamily: "Inter" }}
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "1.2rem",
              fontWeight: "550",
              color: "#0F0718",
            }}
          >
            <img
              src={wallet || "/placeholder.svg"}
              alt="wallet"
              style={{
                width: "24px",
                height: "24px",
                objectFit: "contain",
              }}
            />

            <span>
              {showRefundModal
                ? "Refund Wallet"
                : showTopupModal
                  ? "Top-up Wallet"
                  : showTransactionsModal
                    ? `Transaction History - ${selectedCustomer[0]?.name || "Customer"}`
                    : "Customer Wallet Management"}
            </span>
          </div>
          <img
            src={Close || "/placeholder.svg"}
            onClick={() => {
              if (showTopupModal || showTransactionsModal || showRefundModal) {
                setShowTopupModal(false);
                setShowTransactionsModal(false);
                setShowRefundModal(false);
                setTopupAmount("");
                setCurrentCashInput("");
                setCurrentCardInput("");
                setCurrentUPIInput("");
                setDynamicPaymentInput("");
                setCashTendered(0);
                setShowCardAmountInput(false);
                cardPaymentForm.resetFields();
                useTransactionStore.getState().setWalletPayments([]);
                localStorage.removeItem("walletDataForCardIntegration");
                setSelectedPaymentMethod({});
                restoreInitialPayments();
              } else {
                setShowCustomerWallet(false);
                setSelectedCustomer(null);
                setCustomerSearchValue("");
                setShowTopupModal(false);
                setShowTransactionsModal(false);
                setTopupAmount("");
                setCurrentCashInput("");
                setCurrentCardInput("");
                setCurrentUPIInput("");
                setDynamicPaymentInput("");
                setCashTendered(0);
                setShowCardAmountInput(false);
                cardPaymentForm.resetFields();
                useTransactionStore.getState().setWalletPayments([]);
                localStorage.removeItem("walletDataForCardIntegration");
                setSelectedPaymentMethod({});
                restoreInitialPayments();
              }
            }}
            style={{ cursor: "pointer", width: "1.5vw" }}
            alt="Close"
          />
        </div>
      }
    >
      {showTopupModal || showRefundModal ? (
        <div style={{ marginLeft: "1.5em", marginRight: "1.5em" }}>
          <div
            style={{
              height: "3em",
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              verticalAlign: "center",
              fontSize: "1rem",
              backgroundColor: "#f0f0f0",
              color: "#28a745",
              fontWeight: "bold",
              marginTop: "0.3rem",
              borderRadius: "12px",
            }}
          >
            Current Balance: {currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""}
            {selectedCustomer[0]?.currentBalance?.toFixed(2) || "0.00"}
            {currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}
          </div>
          <Card
            style={{
              borderRadius: "8px",
              border: "none",
              background: "transparent",
            }}
            bodyStyle={{
              paddingTop: "4px",
              paddingBottom: "12px",
              paddingLeft: 0,
              paddingRight: 0,
            }}
          >
            <Form layout="vertical" onFinish={handleCardPayment} form={cardPaymentForm}>
              <Row gutter={[10, 10]}>
                <Col span={24}>
                  <div>
                    <label style={{ display: "block", marginBottom: "0.3rem", marginTop: "0.1rem", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>
                      {showRefundModal ? "Refund Amount" : "Top-up Amount"}
                    </label>
                    <Input
                      ref={topupInputRef}
                      size="large"
                      value={topupAmount}
                      onChange={(e) => handleTopupAmountChange(e.target.value)}
                      onBlur={formatOnBlur}
                      onFocus={() => setFocusedInputField("topupAmount")}
                      placeholder="0"
                      style={{ textAlign: "right", fontSize: "1rem", borderRadius: "6px" }}
                    />
                  </div>
                </Col>
                <Col span={24}>
                  <Scrollbars style={{ height: "6vh" }}>
                    {matchedPayments?.length > 0 ? (
                      matchedPayments.map((item, index) => (
                        <Row key={index} style={{ fontSize: "1vw" }}>
                          <Col span={12} style={{ display: "flex", alignItems: "center", paddingLeft: "0.8vw", fontWeight: "450", textAlign: "left" }}>
                            <p style={{ margin: "0" }}>{item.name}</p>
                          </Col>
                          <Col span={12} style={{ display: "flex", alignItems: "center", paddingRight: "1vw", textAlign: "right", justifyContent: "end" }}>
                            <p style={{ textAlign: "right", margin: "0", fontWeight: "500", paddingRight: "1.2vw" }}>
                              {currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""}
                              &nbsp;{parseFloat(item.amount).toFixed(pricePrecision)}
                              {currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}
                            </p>
                          </Col>
                        </Row>
                      ))
                    ) : (
                      <Row style={{ fontSize: "1vw", textAlign: "center" }}>
                        <Col span={24}>
                          <p style={{ margin: "0", color: "#888" }}>No payments added</p>
                        </Col>
                      </Row>
                    )}
                  </Scrollbars>
                </Col>
                <Col span={24}>
                  <div>
                    <label style={{ display: "block", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>Payment Method</label>
                    <div style={{ display: "flex", gap: 5 }}>
                      {(() => {
                        // Filter payment methods for wallet topup/refund using walletPreferences.
                        // walletPreferences (JSON string on tillData.tillAccess) lists allowed methods
                        // with isTopup/isRefund flags. If not configured, fall back to standard methods.
                        const allMethods = tillData?.tillAccess?.csBunit?.paymentMethodList || [];
                        const standardMethods = allMethods.filter(
                          (pm) => pm.isWallet !== "Y" && pm.isGiftCard !== "Y" && pm.isGiftCard !== true && pm.isBankTransfer !== "Y" && pm.name?.toLowerCase() !== "complete order"
                        );

                        let walletPaymentSettings = null;
                        try {
                          const raw = tillData?.tillAccess?.walletPreferences;
                          const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                          const prefs = Array.isArray(parsed) ? parsed[0] : parsed;
                          walletPaymentSettings = prefs?.paymentsettings || null;
                        } catch {
                          /* ignore parse errors, use fallback */
                        }

                        let filtered;
                        if (showRefundModal) {
                          if (Array.isArray(walletPaymentSettings) && walletPaymentSettings.length > 0) {
                            const refundIds = walletPaymentSettings.filter((s) => s.refund === "Y").map((s) => s.fin_paymentmethod_id);
                            if (refundIds.length > 0) {
                              filtered = allMethods.filter((pm) => refundIds.includes(pm.finPaymentmethodId));
                            }
                          }
                          if (!filtered || filtered.length === 0) {
                            const explicit = allMethods.filter((pm) => pm.isReturns === "Y" && pm.isWallet !== "Y");
                            filtered = explicit.length > 0 ? explicit : standardMethods;
                          }
                        } else {
                          if (Array.isArray(walletPaymentSettings) && walletPaymentSettings.length > 0) {
                            const topupIds = walletPaymentSettings.filter((s) => s.topup === "Y").map((s) => s.fin_paymentmethod_id);
                            if (topupIds.length > 0) {
                              filtered = allMethods.filter((pm) => topupIds.includes(pm.finPaymentmethodId));
                            }
                          }
                          if (!filtered || filtered.length === 0) {
                            filtered = standardMethods;
                          }
                        }
                        return sortBy(filtered, ["sequenceNo", "name"]);
                      })().map((pm) => (
                        <Button
                          key={pm.name}
                          type={selectedPaymentMethod?.name === pm.name ? "primary" : "default"}
                          onClick={() => {
                            setCurrentCashInput("");
                            setCurrentCardInput("");
                            setCurrentUPIInput("");
                            setCashTendered(0);
                            cardPaymentForm.resetFields();
                            setDenaminationsKeyboard(pm.name.toLowerCase() === "cash");
                            setFocusedInputField(null);
                            setSelectedPaymentMethod(pm);
                            autoFillEnterAmount(topupAmount);
                            if (pm.isBankTransfer === "Y" && setIsBankTransferOpen && setBankTransferContext) {
                              setDenaminationsKeyboard(false);
                              setBankTransferContext({ type: showRefundModal ? "walletrefund" : "wallettopup", paymentMethod: pm, amount: topupAmount });
                              setIsBankTransferOpen(true);
                              return;
                            }
                            setShowCardAmountInput(pm.name.toLowerCase() === "card" && posConfig?.transactionID === "Y" ? false : true);
                            setTimeout(() => {
                              if (pm.name.toLowerCase() === "cash") {
                                cashInputRef.current?.focus();
                                setFocusedInputField("currentCashInput");
                              } else if (pm.name.toLowerCase() === "card" && (posConfig?.transactionID !== "Y" || showCardAmountInput)) {
                                cardInputRef.current?.focus();
                                setFocusedInputField("currentCardInput");
                              } else if (pm.name.toLowerCase() === "upi") {
                                upiInputRef.current?.focus();
                                setFocusedInputField("currentUPIInput");
                              }
                            }, 0);
                          }}
                          style={{
                            flex: 1,
                            height: "40px",
                            fontSize: "0.9rem",
                            backgroundColor: selectedPaymentMethod?.name === pm.name ? "#2F3856" : themeJSON.paymentMethodsBGColor || "#fff",
                            color: selectedPaymentMethod?.name === pm.name ? "#fff" : "#0F0718",
                            borderColor: themeJSON.paymentMethodsBGColor || "#e8e8e8",
                            borderRadius: "7px",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <span>
                              {pm.name.toLowerCase() === "cash" ? (
                                <>
                                  {currenciesList[0]?.currSymbol || ""} {pm.name}
                                </>
                              ) : pm.name.toLowerCase() === "card" ? (
                                <>
                                  <img src={cardType} style={{ height: "15px", marginRight: "5px" }} alt="card" />
                                  {pm.name}
                                </>
                              ) : pm.name.toLowerCase() === "upi" ? (
                                <>
                                  <img src={UPI} style={{ height: "15px", marginRight: "5px" }} alt="upi" />
                                  {pm.name}
                                </>
                              ) : (
                                <>{pm.name}</>
                              )}
                            </span>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </div>
                </Col>
                {selectedPaymentMethod?.name?.toLowerCase() === "cash" && denaminationsKeyboard ? (
                  <Col span={24}>
                    <div>
                      <label style={{ display: "block", marginBottom: "0.3rem", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>Enter Amount</label>
                      <div>
                        <Row>
                          <Input
                            className="productSearch sm-payment-search totalQtyInput"
                            style={{
                              borderRadius: "5px",
                              height: "6vh",
                              margin: "0 0% 2.7% 0%",
                              textAlign: "right",
                              fontWeight: "500",
                              fontSize: "2.5em",
                              borderColor: "#fff",
                              color: "#2F3856",
                              fontFamily: "Inter",
                              border: "1px solid #f0f0f0",
                            }}
                            value={currentCashInput}
                            id="sm-amount-input"
                            onChange={(e) => setCurrentCashInput(e.target.value?.replace(/[^\d.]/g, ""))}
                            onFocus={() => setFocusedInputField("currentCashInput")}
                            ref={cashInputRef}
                            placeholder="0"
                          />
                        </Row>
                        <Row style={{ padding: "0 0% 0 0.5%" }}>
                          <Col span={18}>
                            <Row gutter={[8, 5]} style={{ display: "flex", flexDirection: "row-reverse" }}>
                              {currenciesData.map((currency) => (
                                <Col span={8} key={currency.value}>
                                  <button
                                    id={`sm-amount-button${currenciesData.indexOf(currency) + 1}`}
                                    className="amt-dial-btn-market"
                                    onClick={() => handleDenominationClick(currency.value)}
                                    disabled={currency.value === 999999}
                                    style={{ color: currency.value !== 999999 ? "#0f0718" : "transparent", background: themeJSON?.keyBoardColor }}
                                  >
                                    {currency.value}
                                  </button>
                                </Col>
                              ))}
                            </Row>
                          </Col>
                          <Col span={6}>
                            <Row style={{ padding: "0 0 2% 4%" }}>
                              <button
                                className="amt-dial-btn-market"
                                id="sm-amount-buttonRemove"
                                onClick={() => handleTotalQty("x")}
                                style={{ background: themeJSON?.keyBoardColor, justifyContent: "center", alignItems: "center", display: "flex" }}
                              >
                                <img src={CancelArrow} alt="back space" width={25} height={15} />
                              </button>
                            </Row>
                            <Row style={{ padding: "0 0 2% 4%" }}>
                              <button
                                id="sm-amount-buttonClear"
                                className="amt-dial-btn-market"
                                onClick={() => {
                                  handleTotalQty("clear");
                                  setDenaminationsKeyboard(false);
                                  setLayoutName("numbers");
                                }}
                                style={{ background: themeJSON?.keyBoardColor }}
                              >
                                1,2,3...
                              </button>
                            </Row>
                            <Row style={{ padding: "0 0 0 4%" }}>
                              <button id="sm-amount-buttonEnter" className="amt-dial-btn-market2" disabled={isProcessingTopup} onClick={enterTotalQty} style={{ background: themeJSON?.keyBoardColor }}>
                                Enter
                              </button>
                            </Row>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </Col>
                ) : selectedPaymentMethod?.name?.toLowerCase() === "cash" && !denaminationsKeyboard ? (
                  <Col span={24}>
                    <div>
                      <label style={{ display: "block", marginBottom: "0.3rem", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>Enter Amount</label>
                      <div>
                        <Row>
                          <Input
                            className="productSearch sm-payment-search totalQtyInput"
                            style={{
                              borderRadius: "5px",
                              height: "6vh",
                              margin: "0 0% 2.7% 0%",
                              textAlign: "right",
                              fontWeight: "500",
                              fontSize: "2.5em",
                              borderColor: "#fff",
                              color: "#2F3856",
                              fontFamily: "Inter",
                              border: "1px solid #f0f0f0",
                            }}
                            value={currentCashInput}
                            id="sm-amount-input"
                            onChange={(e) => setCurrentCashInput(e.target.value?.replace(/[^\d.]/g, ""))}
                            onFocus={() => setFocusedInputField("currentCashInput")}
                            ref={cashInputRef}
                            placeholder="0"
                          />
                        </Row>
                        <Row style={{ padding: "0 0% 0 0.5%" }} gutter={[8, 6]}>
                          {["1", "2", "3"].map((n) => (
                            <Col span={6} key={n}>
                              <button
                                id={`sm-amount-button${n}`}
                                className="amt-dial-btn-market"
                                style={{ background: themeJSON?.keyBoardColor }}
                                onClick={() => handleTotalQty(n)}
                              >
                                {n}
                              </button>
                            </Col>
                          ))}
                          <Col span={6}>
                            <button
                              className="amt-dial-btn-market"
                              id="sm-amount-buttonRemove"
                              style={{ background: themeJSON?.keyBoardColor, justifyContent: "center", alignItems: "center", display: "flex" }}
                              onClick={() => handleTotalQty("x")}
                            >
                              <img src={CancelArrow} alt="back space" width={25} height={15} />
                            </button>
                          </Col>
                          {["4", "5", "6"].map((n) => (
                            <Col span={6} key={n}>
                              <button
                                id={`sm-amount-button${n}`}
                                className="amt-dial-btn-market"
                                style={{ background: themeJSON?.keyBoardColor }}
                                onClick={() => handleTotalQty(n)}
                              >
                                {n}
                              </button>
                            </Col>
                          ))}
                          <Col span={6}>
                            <button
                              id="sm-amount-buttonClear"
                              className="amt-dial-btn-market"
                              style={{ background: themeJSON?.keyBoardColor }}
                              onClick={() => {
                                setDenaminationsKeyboard(true);
                                setLayoutName("numbers");
                              }}
                            >
                              1,2,3...
                            </button>
                          </Col>
                          <Col span={18}>
                            <Row gutter={[8, 6]}>
                              {["7", "8", "9"].map((n) => (
                                <Col span={8} key={n}>
                                  <button
                                    id={`sm-amount-button${n}`}
                                    className="amt-dial-btn-market"
                                    style={{ background: themeJSON?.keyBoardColor }}
                                    onClick={() => handleTotalQty(n)}
                                  >
                                    {n}
                                  </button>
                                </Col>
                              ))}
                              <Col span={8}>
                                <button id="sm-amount-button0" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleTotalQty("0")}>
                                  0
                                </button>
                              </Col>
                              <Col span={8}>
                                <button
                                  id="sm-amount-button00"
                                  className="amt-dial-btn-market"
                                  style={{ background: themeJSON?.keyBoardColor }}
                                  onClick={() => handleTotalQty(".")}
                                >
                                  .
                                </button>
                              </Col>
                              <Col span={8}>
                                <button
                                  id="sm-amount-button-clear"
                                  className="amt-dial-btn-market"
                                  style={{ background: themeJSON?.keyBoardColor }}
                                  onClick={() => handleTotalQty("clear")}
                                >
                                  Clear
                                </button>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={6}>
                            <button id="sm-amount-buttonEnter" className="amt-dial-btn-market2" disabled={isProcessingTopup} style={{ background: themeJSON?.keyBoardColor }} onClick={enterTotalQty}>
                              Enter
                            </button>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </Col>
                ) : selectedPaymentMethod?.name?.toLowerCase() === "card" && posConfig?.transactionID === "Y" && !showCardAmountInput && showCardDetailsModal ? (
                  <Row>
                    <Col span={10} style={{ paddingRight: "1vw" }}>
                      <Row>
                        <Col span={20}>
                          <Form.Item
                            style={{ marginBottom: "10px" }}
                            name="transactionId"
                            rules={[{ required: posConfig?.transactionID_mandatory === "Yes", message: "Please enter Transaction ID" }]}
                            label={<span style={{ color: "#000", fontSize: "1rem", fontWeight: "500" }}>Transaction ID</span>}
                          >
                            <Input
                              id="transactionId"
                              placeholder="Transaction ID"
                              onFocus={() => {
                                setInputFocused("transactionId");
                                setLayoutName("default");
                              }}
                              size="large"
                              onPressEnter={(e) => e.preventDefault()}
                              allowClear={false}
                              style={{ borderRadius: "6px", padding: "8px 12px" }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={20}>
                          {posConfig?.authorizationCode === "Y" && (
                            <Form.Item
                              style={{ marginBottom: "10px" }}
                              name="authorization"
                              rules={[{ required: posConfig?.authorizationCode_mandatory === "Yes", message: "Please enter Authorization Code" }]}
                              label={<span style={{ color: "#000", fontSize: "1rem", fontWeight: "500" }}>Authorization Code</span>}
                            >
                              <Input
                                id="pin"
                                ref={authorizationRef}
                                placeholder="Authorization Code"
                                size="large"
                                onPressEnter={(e) => e.preventDefault()}
                                onFocus={() => {
                                  setInputFocused("authorization");
                                  setLayoutName("default");
                                }}
                                allowClear={false}
                                style={{ borderRadius: "6px", padding: "8px 12px" }}
                              />
                            </Form.Item>
                          )}
                        </Col>
                        <Col span={20}>
                          {posConfig?.cardType === "Y" && (
                            <Form.Item
                              style={{ marginBottom: "10px" }}
                              name="cardType"
                              rules={[{ required: posConfig?.cardType_mandatory === "Yes", message: "Please select Card Type" }]}
                              label={<span style={{ color: "#000", fontSize: "1rem", fontWeight: "500" }}>Card Type</span>}
                            >
                              <Select className="selecItem" placeholder={<span className="cartSelect">Card Type</span>} size="large" style={{ borderRadius: "6px" }}>
                                <Option className="cartOption" key="VI" value="VI">
                                  Visa - VI
                                </Option>
                                <Option className="cartOption" key="MA" value="MA">
                                  Master Card - MA
                                </Option>
                                <Option className="cartOption" key="RU" value="RU">
                                  Rupay - RU
                                </Option>
                                <Option className="cartOption" key="AM" value="AM">
                                  Amex - AM
                                </Option>
                                <Option className="cartOption" key="ME" value="ME">
                                  Maestro - ME
                                </Option>
                              </Select>
                            </Form.Item>
                          )}
                        </Col>
                        <Col span={20}>
                          {posConfig?.CardNumber === "Y" && (
                            <Form.Item
                              style={{ marginBottom: "10px" }}
                              name="cardNumber"
                              rules={[
                                { required: posConfig?.CardNumber_mandatory === "Yes", message: "Please enter Card Number" },
                                { pattern: /^\d{4}$/, message: "Card Number must be exactly 4 digits" },
                              ]}
                              label={<span style={{ color: "#000", fontSize: "1rem", fontWeight: "500" }}>Card Number (Last 4 Digits)</span>}
                            >
                              <Input
                                id="redeemNumber"
                                placeholder="Enter Number"
                                onFocus={() => {
                                  setInputFocused("cardNumber");
                                  setLayoutName("numbers");
                                }}
                                size="large"
                                onPressEnter={(e) => e.preventDefault()}
                                allowClear={false}
                                style={{ borderRadius: "6px", padding: "8px 12px" }}
                                maxLength={4}
                              />
                            </Form.Item>
                          )}
                        </Col>
                      </Row>
                    </Col>
                    <Col span={14}>
                      <Row gutter={[8, 6]}>
                        <Keyboard
                          theme={"hg-theme-default aprvl"}
                          style={{ marginTop: "5px" }}
                          onChangeAll={(inputs) => {
                            cardPaymentForm.setFieldsValue(inputs);
                          }}
                          inputName={inputFocused}
                          keyboardRef={(r) => (keyboardRef.current = r)}
                          onKeyPress={(button) => {
                            const currentValue = cardPaymentForm.getFieldValue(inputFocused) || "";
                            if (inputFocused === "cardNumber") {
                              if (button.match(/^\d$/)) {
                                if (currentValue.length < 4) {
                                  cardPaymentForm.setFieldsValue({ cardNumber: currentValue + button });
                                }
                                return;
                              } else if (button === "{backspace}") {
                                cardPaymentForm.setFieldsValue({ [inputFocused]: currentValue.slice(0, -1) });
                              }
                              return;
                            }
                            if (button === "{shift}" || button === "{lock}") handleShift();
                            else if (button === "{number}" || button === "{numbers}") setLayoutName("numbers");
                            else if (button === "{small}") setLayoutName("shift");
                            else if (button === "{caps}") setLayoutName("default");
                            else if (button === "{space}") cardPaymentForm.setFieldsValue({ [inputFocused]: `${currentValue} ` });
                            else if (button === "{backspace}") cardPaymentForm.setFieldsValue({ [inputFocused]: currentValue.slice(0, -1) });
                            else if (button !== "{enter}" && button !== "{shift}" && button !== "{caps}" && button !== "{numbers}") handleKeyboardInputs(button);
                          }}
                          layoutName={layoutName}
                          display={{
                            "{abc}": "ABC",
                            "{smileys}": "\uD83D\uDE03",
                            "{back}": "Enter",
                            "{numbers}": "123",
                            "{number}": "123",
                            "{bksp}": "backspace",
                            "{backspace}": "\u232B",
                            "{shift}": "\u21E7",
                            "{tab}": "tab",
                            "{lock}": "lock",
                            "{enter}": "Enter",
                            "{space}": "Space",
                            "{caps}": "ABC",
                            "{small}": "abc",
                          }}
                          layout={{
                            shift: ["q w e r t y u i o p", "a s d f g h j k l", "{shift} z x c v b n m {backspace}", "{number} {space} {enter}"],
                            numbers: ["1 2 3 4 5 6 7 8 9 0", "! @ # $ % ^ & * ( )", "- . , _ / ` {backspace}", "{caps} {space} {enter}"],
                            number: ["1 2 3 4 5 6 7 8 9 0", "! @ # $ % ^ & * ( )", "- . , _ / ` {backspace}", "{small} {space} {enter}"],
                            default: ["Q W E R T Y U I O P", "A S D F G H J K L", "{shift} Z X C V B N M {backspace}", "{numbers} {space} {enter}"],
                          }}
                        />
                        <Col span={20} pull={2} push={2} style={{ padding: "3% 0", marginTop: "1%" }}>
                          <Button
                            htmlType="button"
                            onClick={handleCardDetailsSubmit}
                            disabled={isProcessingTopup}
                            loading={isProcessingTopup}
                            style={{
                              backgroundColor: "#2F3856",
                              color: "#fff",
                              width: "100%",
                              fontSize: "1rem",
                              textAlign: "center",
                              fontWeight: "400",
                              borderRadius: "7px",
                              height: "40px",
                            }}
                          >
                            Process Payment
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                ) : selectedPaymentMethod?.name?.toLowerCase() === "card" ? (
                  <Col span={24}>
                    <div>
                      <label style={{ display: "block", marginBottom: "0.3rem", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>Enter Amount</label>
                      <div>
                        <Row>
                          <Input
                            className="productSearch sm-payment-search totalQtyInput"
                            style={{
                              borderRadius: "5px",
                              height: "6vh",
                              margin: "0 0% 2.7% 0%",
                              textAlign: "right",
                              fontWeight: "500",
                              fontSize: "2.5em",
                              borderColor: "#fff",
                              color: "#2F3856",
                              fontFamily: "Inter",
                              border: "1px solid #f0f0f0",
                            }}
                            value={currentCardInput}
                            id="sm-amount-input"
                            onChange={(e) => setCurrentCardInput(e.target.value?.replace(/[^\d.]/g, ""))}
                            onFocus={() => setFocusedInputField("currentCardInput")}
                            ref={cardInputRef}
                            placeholder="0"
                          />
                        </Row>
                        <Row style={{ padding: "0 0% 0 0.5%" }} gutter={[4, 3]}>
                          {["1", "2", "3"].map((n) => (
                            <Col span={6} key={n}>
                              <button
                                id={`sm-amount-button${n}`}
                                className="amt-dial-btn-market"
                                style={{ background: themeJSON?.keyBoardColor }}
                                onClick={() => handleTotalQty(n)}
                              >
                                {n}
                              </button>
                            </Col>
                          ))}
                          <Col span={6}>
                            <button
                              className="amt-dial-btn-market"
                              id="sm-amount-buttonRemove"
                              style={{ background: themeJSON?.keyBoardColor, justifyContent: "center", alignItems: "center", display: "flex" }}
                              onClick={() => handleTotalQty("x")}
                            >
                              <img src={CancelArrow} alt="back space" width={25} height={15} />
                            </button>
                          </Col>
                          {["4", "5", "6"].map((n) => (
                            <Col span={6} key={n}>
                              <button
                                id={`sm-amount-button${n}`}
                                className="amt-dial-btn-market"
                                style={{ background: themeJSON?.keyBoardColor }}
                                onClick={() => handleTotalQty(n)}
                              >
                                {n}
                              </button>
                            </Col>
                          ))}
                          <Col span={6}>
                            <button
                              id="sm-amount-buttonClear"
                              className="amt-dial-btn-market"
                              style={{ background: themeJSON?.keyBoardColor }}
                              onClick={() => {
                                setDenaminationsKeyboard(true);
                                setLayoutName("numbers");
                              }}
                            >
                              1,2,3...
                            </button>
                          </Col>
                          <Col span={18}>
                            <Row gutter={[4, 3]}>
                              {["7", "8", "9"].map((n) => (
                                <Col span={8} key={n}>
                                  <button
                                    id={`sm-amount-button${n}`}
                                    className="amt-dial-btn-market"
                                    style={{ background: themeJSON?.keyBoardColor }}
                                    onClick={() => handleTotalQty(n)}
                                  >
                                    {n}
                                  </button>
                                </Col>
                              ))}
                              <Col span={8}>
                                <button id="sm-amount-button0" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleTotalQty("0")}>
                                  0
                                </button>
                              </Col>
                              <Col span={8}>
                                <button
                                  id="sm-amount-button00"
                                  className="amt-dial-btn-market"
                                  style={{ background: themeJSON?.keyBoardColor }}
                                  onClick={() => handleTotalQty(".")}
                                >
                                  .
                                </button>
                              </Col>
                              <Col span={8}>
                                <button
                                  id="sm-amount-button-clear"
                                  className="amt-dial-btn-market"
                                  style={{ background: themeJSON?.keyBoardColor }}
                                  onClick={() => handleTotalQty("clear")}
                                >
                                  Clear
                                </button>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={6}>
                            <button
                              id="sm-amount-buttonEnter"
                              className="amt-dial-btn-market2"
                              disabled={isProcessingTopup}
                              style={{ background: themeJSON?.keyBoardColor }}
                              onClick={() => {
                                enterTotalQtyForWallet();
                              }}
                            >
                              Enter
                            </button>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </Col>
                ) : selectedPaymentMethod?.name?.toLowerCase() === "upi" ? (
                  <Col span={24}>
                    <div>
                      <label style={{ display: "block", marginBottom: "0.3rem", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>Enter Amount</label>
                      <div>
                        <Row>
                          <Input
                            className="productSearch sm-payment-search totalQtyInput"
                            style={{
                              borderRadius: "5px",
                              height: "6vh",
                              margin: "0 0% 2.7% 0%",
                              textAlign: "right",
                              fontWeight: "500",
                              fontSize: "2.5em",
                              borderColor: "#fff",
                              color: "#2F3856",
                              fontFamily: "Inter",
                              border: "1px solid #f0f0f0",
                            }}
                            value={currentUPIInput}
                            id="sm-amount-input"
                            onChange={(e) => setCurrentUPIInput(e.target.value?.replace(/[^\d.]/g, ""))}
                            onFocus={() => setFocusedInputField("currentUPIInput")}
                            ref={upiInputRef}
                            placeholder="0"
                          />
                        </Row>
                        <Row style={{ padding: "0 0% 0 0.5%" }} gutter={[4, 3]}>
                          {["1", "2", "3"].map((n) => (
                            <Col span={6} key={n}>
                              <button
                                id={`sm-amount-button${n}`}
                                className="amt-dial-btn-market"
                                style={{ background: themeJSON?.keyBoardColor }}
                                onClick={() => handleTotalQty(n)}
                              >
                                {n}
                              </button>
                            </Col>
                          ))}
                          <Col span={6}>
                            <button
                              className="amt-dial-btn-market"
                              id="sm-amount-buttonRemove"
                              style={{ background: themeJSON?.keyBoardColor, justifyContent: "center", alignItems: "center", display: "flex" }}
                              onClick={() => handleTotalQty("x")}
                            >
                              <img src={CancelArrow} alt="back space" width={25} height={15} />
                            </button>
                          </Col>
                          {["4", "5", "6"].map((n) => (
                            <Col span={6} key={n}>
                              <button
                                id={`sm-amount-button${n}`}
                                className="amt-dial-btn-market"
                                style={{ background: themeJSON?.keyBoardColor }}
                                onClick={() => handleTotalQty(n)}
                              >
                                {n}
                              </button>
                            </Col>
                          ))}
                          <Col span={6}>
                            <button
                              id="sm-amount-buttonClear"
                              className="amt-dial-btn-market"
                              style={{ background: themeJSON?.keyBoardColor }}
                              onClick={() => {
                                setDenaminationsKeyboard(true);
                                setLayoutName("numbers");
                              }}
                            >
                              1,2,3...
                            </button>
                          </Col>
                          <Col span={18}>
                            <Row gutter={[8, 6]}>
                              {["7", "8", "9"].map((n) => (
                                <Col span={8} key={n}>
                                  <button
                                    id={`sm-amount-button${n}`}
                                    className="amt-dial-btn-market"
                                    style={{ background: themeJSON?.keyBoardColor }}
                                    onClick={() => handleTotalQty(n)}
                                  >
                                    {n}
                                  </button>
                                </Col>
                              ))}
                              <Col span={8}>
                                <button id="sm-amount-button0" className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleTotalQty("0")}>
                                  0
                                </button>
                              </Col>
                              <Col span={8}>
                                <button
                                  id="sm-amount-button00"
                                  className="amt-dial-btn-market"
                                  style={{ background: themeJSON?.keyBoardColor }}
                                  onClick={() => handleTotalQty(".")}
                                >
                                  .
                                </button>
                              </Col>
                              <Col span={8}>
                                <button
                                  id="sm-amount-button-clear"
                                  className="amt-dial-btn-market"
                                  style={{ background: themeJSON?.keyBoardColor }}
                                  onClick={() => handleTotalQty("clear")}
                                >
                                  Clear
                                </button>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={6}>
                            <button
                              id="sm-amount-buttonEnter"
                              className="amt-dial-btn-market2"
                              disabled={isProcessingTopup}
                              style={{ background: themeJSON?.keyBoardColor }}
                              onClick={() => {
                                enterTotalQtyForWallet();
                              }}
                            >
                              Enter
                            </button>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </Col>
                ) : selectedPaymentMethod && !["cash", "card", "upi"]?.includes(selectedPaymentMethod?.name?.toLowerCase()) ? (
                  <Col span={24}>
                    <div style={selectedPaymentMethod && selectedPaymentMethod?.isBankTransfer === "Y" ? { opacity: 0.4, userSelect: "none", pointerEvents: "none" } : {}}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "0.3rem",
                          color: "#0F0718",
                          fontWeight: "550",
                          fontSize: "0.9rem",
                        }}
                      >
                        Enter Amount
                      </label>

                      <Input
                        size="large"
                        value={dynamicPaymentInput}
                        onChange={(e) => setDynamicPaymentInput(e.target.value.replace(/[^\d.]/g, ""))}
                        placeholder="0"
                        style={{
                          textAlign: "right",
                          fontSize: "1rem",
                          borderRadius: "6px",
                          marginBottom: "1rem",
                        }}
                      />

                      {renderGenericWalletKeypad()}
                    </div>
                  </Col>
                ) : null}
              </Row>
            </Form>
          </Card>
        </div>
      ) : showTransactionsModal ? (
        <div style={{ marginLeft: "1.5em", marginRight: "1.5em" }}>
          <div style={{ backgroundColor: "#fff", borderRadius: "10px", border: "1px solid #e8e8e8", maxHeight: "87vh", overflowY: "auto" }}>
            {selectedCustomer[0]?.transactions && selectedCustomer[0].transactions.length > 0 ? (
              selectedCustomer[0].transactions
                .sort((a, b) => new Date(`${b.date} ${b.time}`) - new Date(`${a.date} ${a.time}`))
                .map((transaction, index) => (
                  <div
                    key={transaction.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0.5rem",
                      borderBottom: index < selectedCustomer[0].transactions.length - 1 ? "1px solid #f0f0f0" : "none",
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#fff",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span
                          style={{
                            padding: "0.3rem 1rem",
                            borderRadius: "20px",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            backgroundColor: transaction.type === "Credit" ? "#d4edda" : "#f8d7da",
                            color: transaction.type === "Credit" ? "#155724" : "#721c24",
                            marginRight: "0.5rem",
                          }}
                        >
                          {transaction.type}
                        </span>
                        <span style={{ color: "#666", fontSize: "0.8rem" }}>
                          {transaction.date} at {transaction.time}
                        </span>
                      </div>
                      <h4 style={{ margin: "0.3rem 0", color: "#0F0718", fontSize: "1rem" }}>{transaction.description}</h4>
                      <p style={{ margin: 0, color: "#666", fontSize: "0.6rem" }}>Ref: {transaction.reference}</p>
                    </div>
                    <div style={{ textAlign: "right", minWidth: "150px" }}>
                      <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: "bold", color: transaction.type === "Credit" ? "#28a745" : "#dc3545" }}>
                        {transaction.type === "Credit" ? "+" : "-"}
                        {currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""}
                        {transaction.amount.toFixed(2)}
                        {currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}
                      </h3>
                      <p style={{ margin: "0.3rem 0 0 0", color: "#666", fontSize: "0.8rem" }}>
                        Balance: {currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""}
                        {transaction.balance.toFixed(2)}
                        {currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}
                      </p>
                    </div>
                  </div>
                ))
            ) : (
              <div style={{ textAlign: "center", padding: "3rem", color: "#666" }}>
                <h3>No transactions found</h3>
                <p>This customer hasn't made any transactions yet.</p>
              </div>
            )}
          </div>
        </div>
      ) : selectedCustomer ? (
        <div style={{ marginLeft: "1.5em", marginRight: "1.5em" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
              padding: "0.7rem",
              backgroundColor: "#fff",
              borderRadius: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", height: "3vh", marginRight: "1rem" }}>
              <UserOutlined style={{ fontSize: "1.5rem", color: "#2F3856", marginRight: "0.5rem", padding: "0.5rem", backgroundColor: "#f0f0f0", borderRadius: "50%" }} />
              <div>
                <h2 style={{ margin: 0, color: "#0F0718", fontSize: "1rem" }}>{selectedCustomer[0]?.name}</h2>
                <p style={{ margin: "0.2rem 0", color: "#666", fontSize: "0.7rem" }}>
                  {selectedCustomer[0]?.phone && selectedCustomer[0]?.phone !== "N/A" ? `Mobile: ${selectedCustomer[0]?.phone} | ` : ""}ID: {selectedCustomer[0]?.id}
                </p>
              </div>
            </div>
          </div>
          <Row gutter={[12, 12]} style={{ marginBottom: "0.8rem" }}>
            <Col span={8}>
              <Card
                style={{
                  borderRadius: "10px",
                  border: "1px solid #e8e8e8",
                  backgroundColor: "#fff",
                  minHeight: "120px",
                }}
                bodyStyle={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0.7rem",
                }}
              >
                <img
                  src={credit || "/placeholder.svg"}
                  alt="balance"
                  style={{
                    width: "24px",
                    height: "24px",
                    objectFit: "contain",
                    marginBottom: "0.5rem",
                  }}
                />

                <h4
                  style={{
                    margin: 0,
                    color: "#666",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    textAlign: "center",
                  }}
                >
                  Current Balance
                </h4>

                <h2
                  style={{
                    marginTop: "0.4rem",
                    marginBottom: 0,
                    color: "#28a745",
                    fontSize: "1.2rem",
                    fontWeight: "700",
                    textAlign: "center",
                    wordBreak: "break-word",
                  }}
                >
                  {currenciesList?.[0]?.symbolRightSide === "N" ? currenciesList[0].currSymbol : ""}
                  {selectedCustomer?.[0]?.currentBalance?.toFixed(2) || "0.00"}
                  {currenciesList?.[0]?.symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}
                </h2>
              </Card>
            </Col>

            <Col span={8}>
              <Card
                style={{
                  borderRadius: "10px",
                  border: "1px solid #e8e8e8",
                  backgroundColor: "#fff",
                  minHeight: "120px",
                }}
                bodyStyle={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0.7rem",
                }}
              >
                <img
                  src={cash || "/placeholder.svg"}
                  alt="spent"
                  style={{
                    width: "24px",
                    height: "24px",
                    objectFit: "contain",
                    marginBottom: "0.5rem",
                  }}
                />

                <h4
                  style={{
                    margin: 0,
                    color: "#666",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    textAlign: "center",
                  }}
                >
                  Total Spent
                </h4>

                <h2
                  style={{
                    marginTop: "0.4rem",
                    marginBottom: 0,
                    color: "#0F0718",
                    fontSize: "1.2rem",
                    fontWeight: "700",
                    textAlign: "center",
                    wordBreak: "break-word",
                  }}
                >
                  {currenciesList?.[0]?.symbolRightSide === "N" ? currenciesList[0].currSymbol : ""}
                  {selectedCustomer?.[0]?.totalSpent?.toFixed(2) || "0.00"}
                  {currenciesList?.[0]?.symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}
                </h2>
              </Card>
            </Col>

            <Col span={8}>
              <Card
                style={{
                  borderRadius: "10px",
                  border: "1px solid #e8e8e8",
                  backgroundColor: "#fff",
                  minHeight: "120px",
                }}
                bodyStyle={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0.7rem",
                }}
              >
                <CalendarOutlined
                  style={{
                    fontSize: "1.5rem",
                    color: "#2F3856",
                    marginBottom: "0.5rem",
                  }}
                />

                <h4
                  style={{
                    margin: 0,
                    color: "#666",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    textAlign: "center",
                  }}
                >
                  Member Since
                </h4>

                <h2
                  style={{
                    marginTop: "0.4rem",
                    marginBottom: 0,
                    color: "#0F0718",
                    fontSize: "1rem",
                    fontWeight: "700",
                    textAlign: "center",
                    wordBreak: "break-word",
                  }}
                >
                  {selectedCustomer?.[0]?.joinDate ? formatDate(selectedCustomer[0].joinDate) : ""}
                </h2>
              </Card>
            </Col>
          </Row>
          <div style={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                handleTopupWallet();
                setTimeout(() => {
                  topupInputRef.current?.focus();
                }, 0);
              }}
              style={{
                backgroundColor: "#2F3856",
                borderColor: "#2F3856",
                height: "45px",
                width: "25em",
                fontSize: "1.1rem",
                fontWeight: "500",
                borderRadius: "6px",
                paddingLeft: "2rem",
                paddingRight: "2rem",
              }}
            >
              Top-up Wallet
            </Button>
            {posConfig?.enableCustomerWalletRefund === "Y" && (
              <Button
                size="large"
                type="primary"
                onClick={() => handleWalletRefund()}
                style={{
                  backgroundColor: "#fff",
                  color: "#0F0718",
                  height: "45px",
                  width: "25em",
                  fontSize: "1.1rem",
                  fontWeight: "500",
                  borderRadius: "6px",
                  paddingLeft: "2rem",
                  paddingRight: "2rem",
                  border: "1px solid #e8e8e8",
                }}
              >
                Refund Wallet
              </Button>
            )}
            <Button
              size="large"
              onClick={handleViewTransactions}
              icon={<EyeOutlined />}
              style={{ color: "#0F0718", height: "45px", width: "25em", fontSize: "1.1rem", fontWeight: "500", borderRadius: "6px", paddingLeft: "2rem", paddingRight: "2rem" }}
            >
              View Transactions
            </Button>
          </div>
          <div style={{ marginTop: "0.5rem" }}>
            <h3 style={{ color: "#0F0718", fontSize: "1.2rem", fontWeight: "600" }}>Recent Transactions</h3>
            <div style={{ backgroundColor: "#fff", borderRadius: "10px", border: "1px solid #e8e8e8", height: "38vh", overflowY: "auto" }}>
              {selectedCustomer?.[0]?.transactions?.length > 0 && currenciesList?.[0] ? (
                selectedCustomer[0].transactions
                  .sort((a, b) => new Date(`${b.date} ${b.time}`) - new Date(`${a.date} ${a.time}`))
                  .slice(0, 22)
                  .map((transaction, index) => {
                    const currency = currenciesList[0];
                    return (
                      <div
                        key={transaction.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0.5rem",
                          borderBottom: index < 21 ? "1px solid #f0f0f0" : "none",
                          backgroundColor: index % 2 === 0 ? "#fafafa" : "#fff",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
                            <span
                              style={{
                                borderRadius: "20px",
                                fontSize: "0.8rem",
                                fontWeight: "600",
                                backgroundColor: transaction.type === "Credit" ? "#d4edda" : "#f8d7da",
                                color: transaction.type === "Credit" ? "#155724" : "#721c24",
                                marginRight: "0.5rem",
                                padding: "0.2rem 0.6rem",
                              }}
                            >
                              {transaction.type}
                            </span>
                            <span style={{ color: "#666", fontSize: "0.8rem" }}>
                              {transaction.date} at {transaction.time}
                            </span>
                          </div>
                          <h4 style={{ margin: "0.3rem 0", color: "#0F0718", fontSize: "1rem" }}>{transaction.description}</h4>
                          <p style={{ margin: 0, color: "#666", fontSize: "0.6rem" }}>Ref: {transaction.reference}</p>
                        </div>
                        <div style={{ textAlign: "right", minWidth: "120px" }}>
                          <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: "bold", color: transaction.type === "Credit" ? "#28a745" : "#dc3545" }}>
                            {transaction.type === "Credit" ? "+" : "-"}
                            {currency.symbolRightSide === "N" ? currency.currSymbol : ""}
                            {transaction.amount.toFixed(2)}
                            {currency.symbolRightSide === "Y" ? currency.currSymbol : ""}
                          </h3>
                          <p style={{ margin: "0.3rem 0 0 0", color: "#666", fontSize: "0.8rem" }}>
                            Bal: {currency.symbolRightSide === "N" ? currency.currSymbol : ""}
                            {transaction.balance.toFixed(2)}
                            {currency.symbolRightSide === "Y" ? currency.currSymbol : ""}
                          </p>
                        </div>
                      </div>
                    );
                  })
              ) : (
                <div style={{ textAlign: "center", padding: "1rem", color: "#666" }}>
                  <h4>No recent transactions</h4>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ marginLeft: "1.5em", marginRight: "1.5em" }}>
          <Input
            placeholder="Search Customer by Name, Mobile, or ID"
            value={customerSearchValue}
            onChange={(e) => handleCustomerSearch(e.target.value)}
            prefix={<SearchOutlined />}
            style={{ marginBottom: "1rem", height: "40px", borderRadius: "6px" }}
          />
          <div style={{ backgroundColor: "#fff", borderRadius: "10px", border: "1px solid #e8e8e8", maxHeight: "70vh", overflowY: "auto", padding: "1rem" }}>
            {filteredCustomers.length > 0 ? (
              filteredCustomers.map((customer) => (
                <div
                  key={customer.id}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem", borderBottom: "1px solid #f0f0f0", cursor: "pointer" }}
                  onClick={() => handleCustomerSelect(customer)}
                >
                  <div>
                    <h4 style={{ margin: 0, color: "#0F0718", fontSize: "1rem" }}>{customer.name}</h4>
                    <p style={{ margin: "0.2rem 0", color: "#666", fontSize: "0.8rem" }}>
                      Mobile: {customer.phone} | ID: {customer.id}
                    </p>
                  </div>
                  <span style={{ color: "#28a745", fontSize: "0.9rem" }}></span>
                </div>
              ))
            ) : (
              <div style={{ textAlign: "center", padding: "3rem", color: "#666" }}>
                <h3>No customers found</h3>
                <p>Try searching with a different keyword.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default CustomerWalletDrawer;
